import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";

const VEHICLE_TYPES = [
  { value: "car",             label: "Car" },
  { value: "motorcycle",      label: "Motorcycle / Moped" },
  { value: "pickup",          label: "Pickup truck" },
  { value: "small_van",       label: "Small van" },
  { value: "swb_van",         label: "SWB van" },
  { value: "mwb_van",         label: "MWB van" },
  { value: "lwb_van",         label: "LWB van" },
  { value: "xlwb_van",        label: "XLWB van" },
  { value: "luton_van",       label: "Luton van" },
  { value: "hgv_75t",         label: "7.5 Tonne (C1)" },
  { value: "hgv_18t",         label: "18 Tonne (C)" },
  { value: "hgv_26t",         label: "26 Tonne (C)" },
  { value: "hgv_artic",       label: "Articulated (C+E)" },
  { value: "trailer",         label: "Trailer" },
  { value: "other",           label: "Other" },
];
const VTYPE_LABEL = Object.fromEntries(VEHICLE_TYPES.map(t => [t.value, t.label]));

const EMPTY = {
  name: "", contact_name: "", phone: "", email: "",
  address_line1: "", address_line2: "", city: "", postcode: "", country: "United Kingdom",
  services: "", vehicle_types_serviced: [], notes: "", is_active: true,
};

export default function MaintenanceSuppliers() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/maintenance-suppliers").then(r => setRows(r.data || [])).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    if (q === "active" || q === "inactive") {
      return rows.filter(r => q === "active" ? r.is_active !== false : r.is_active === false);
    }
    return rows.filter(r => {
      const typeLabels = (r.vehicle_types_serviced || []).map(v => VTYPE_LABEL[v] || v);
      return [r.name, r.contact_name, r.phone, r.email, r.address_line1, r.city, r.postcode, r.services, r.notes, ...typeLabels]
        .filter(Boolean).some(s => s.toLowerCase().includes(q));
    });
  }, [rows, search]);

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "", contact_name: r.contact_name || "", phone: r.phone || "", email: r.email || "",
      address_line1: r.address_line1 || "", address_line2: r.address_line2 || "",
      city: r.city || "", postcode: r.postcode || "", country: r.country || "United Kingdom",
      services: r.services || "",
      vehicle_types_serviced: Array.isArray(r.vehicle_types_serviced) ? r.vehicle_types_serviced : [],
      notes: r.notes || "", is_active: r.is_active !== false,
    });
    setOpen(true);
  };
  const onClose = () => { setOpen(false); setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/maintenance-suppliers/${editing.id}`, form);
      else         await api.post("/maintenance-suppliers", form);
      toast.success(editing ? "Supplier updated" : "Supplier added");
      onClose(); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete supplier ${r.name}?`)) return;
    try { await api.delete(`/maintenance-suppliers/${r.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="maintenance-suppliers-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Fleet · Maintenance</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Maintenance suppliers</h1>
          <p className="text-sm text-slate-600 mt-1">Garages, recovery firms and other providers you use. When logging a maintenance record, pick a supplier here to auto-fill the address and contact details.</p>
        </div>
        <Button data-testid="msup-add-btn" onClick={startNew} className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-1.5" /> Add supplier
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="msup-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Search name, postcode, services or "active"/"inactive"' className="pl-9 rounded-sm border-slate-300" />
        </div>
        <div className="text-xs text-slate-500 tabular-nums">{filtered.length} of {rows.length}</div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="maintenance-suppliers-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <Th>Name</Th>
                <Th>Contact</Th>
                <Th>Phone</Th>
                <Th>Address</Th>
                <Th>Services</Th>
                <Th>Status</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500 text-sm">
                    <Wrench className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    {rows.length === 0 ? "No maintenance suppliers yet." : "No suppliers match your search."}
                  </td>
                </tr>
              )}
              {filtered.map(r => (
                <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50">
                  <Td><div className="font-medium text-slate-900">{r.name}</div>{r.email && <div className="text-xs text-slate-500">{r.email}</div>}</Td>
                  <Td><div className="text-slate-900">{r.contact_name || "—"}</div></Td>
                  <Td><div className="tabular-nums text-slate-700">{r.phone || "—"}</div></Td>
                  <Td>
                    <div className="text-slate-700 text-sm">
                      {[r.address_line1, r.address_line2, r.city, r.postcode].filter(Boolean).join(", ") || "—"}
                    </div>
                  </Td>
                  <Td>
                    {r.services && <div className="text-slate-700 text-xs mb-1">{r.services}</div>}
                    {(r.vehicle_types_serviced || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(r.vehicle_types_serviced || []).slice(0, 4).map(vt => (
                          <span key={vt} className="inline-block text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded-sm">
                            {VTYPE_LABEL[vt] || vt}
                          </span>
                        ))}
                        {(r.vehicle_types_serviced || []).length > 4 && (
                          <span className="text-[10px] text-slate-500">+{(r.vehicle_types_serviced || []).length - 4} more</span>
                        )}
                      </div>
                    ) : (!r.services && <span className="text-slate-400">—</span>)}
                  </Td>
                  <Td>
                    {r.is_active === false
                      ? <Badge variant="outline" className="rounded-sm">Inactive</Badge>
                      : <Badge className="rounded-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Active</Badge>}
                  </Td>
                  <Td className="text-right pr-4">
                    <Button size="sm" variant="ghost" data-testid={`msup-edit-${r.id}`} onClick={() => startEdit(r)} className="rounded-sm h-8 w-8 p-0">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`msup-delete-${r.id}`} onClick={() => remove(r)} className="rounded-sm h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent data-testid="msup-dialog" className="rounded-sm sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit supplier" : "Add maintenance supplier"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2">
              <Field label="Supplier name *">
                <Input data-testid="msup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Smith's Motors Ltd" className="rounded-sm border-slate-300" />
              </Field>
            </div>
            <Field label="Contact name">
              <Input data-testid="msup-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Phone">
              <Input data-testid="msup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Email">
                <Input data-testid="msup-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm border-slate-300" />
              </Field>
            </div>
            <Field label="Address line 1">
              <Input data-testid="msup-addr1" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Address line 2">
              <Input data-testid="msup-addr2" value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="City">
              <Input data-testid="msup-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Postcode">
              <Input data-testid="msup-postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Country">
              <Input data-testid="msup-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Active">
              <div className="h-10 flex items-center gap-3">
                <Switch data-testid="msup-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-sm text-slate-600">{form.is_active ? "Active" : "Inactive"}</span>
              </div>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Services (free text)">
                <Input data-testid="msup-services" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} placeholder="MOT, Service, Tyres, Brakes…" className="rounded-sm border-slate-300" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Vehicle types they work on">
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/50" data-testid="msup-vtypes">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-slate-600">Tick every vehicle type this supplier handles.</div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="text-xs text-slate-700 underline hover:text-slate-900"
                        data-testid="msup-vtypes-all"
                        onClick={() => setForm({ ...form, vehicle_types_serviced: VEHICLE_TYPES.map(t => t.value) })}
                      >Select all</button>
                      <span className="text-slate-300">·</span>
                      <button
                        type="button"
                        className="text-xs text-slate-700 underline hover:text-slate-900"
                        data-testid="msup-vtypes-none"
                        onClick={() => setForm({ ...form, vehicle_types_serviced: [] })}
                      >Clear</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {VEHICLE_TYPES.map(t => {
                      const checked = form.vehicle_types_serviced.includes(t.value);
                      const toggle = () => {
                        const next = checked
                          ? form.vehicle_types_serviced.filter(v => v !== t.value)
                          : [...form.vehicle_types_serviced, t.value];
                        setForm({ ...form, vehicle_types_serviced: next });
                      };
                      return (
                        <label key={t.value} className="flex items-center gap-2 cursor-pointer text-sm text-slate-800">
                          <Checkbox
                            data-testid={`msup-vtype-${t.value}`}
                            checked={checked}
                            onCheckedChange={toggle}
                            className="rounded-sm"
                          />
                          <span>{t.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Notes">
                <Textarea data-testid="msup-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-slate-300" />
              </Field>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button data-testid="msup-save" onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800">
              {saving ? "Saving…" : (editing ? "Save changes" : "Add supplier")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`text-left text-[11px] uppercase tracking-wider font-semibold px-3 py-2.5 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-3 align-top ${className}`}>{children}</td>; }
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
